import React, { Component, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import withAuth from 'features/auth/withAuth';
import NavBar from 'features/common/NavBar';
import Footer from 'features/common/Footer';
import T from 'i18n';
import history from 'common/history';
import api from 'common/api';
import * as actions from '../redux/actions';
import * as actionsV from 'features/viewing/redux/actions';
import { bindActionCreators } from 'redux';
import Logger from 'utils/logger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faCircle, faHome } from '@fortawesome/free-solid-svg-icons';
import {
  selectBundleTabPage,
  selectConfidentialityRing,
  selectCurrentCaseId,
  selectCurrentUserConfidentialityRing,
  selectFolderContainers,
  selectViewerControl,
  selectDocSearchVal,
  selectDocSearchLocation,
  selectFileToCompare,
} from 'common/selectors';
import { getHighlightedText } from 'utils/highlightText';
import { fetchFolderSpecificFileDetails } from '../redux/fetchFolderSpecificFileDetails';
import { fetchFileDetails } from '../redux/fetchFileDetails';
import CircularProgress from '@mui/material/CircularProgress';
import FileViewer from '../FileViewer';
import { cfRingColorMapping } from 'features/case/TableCase/TableInfo';
import { RightSidebarViewer } from 'features/viewing';
import { useScrollToHighlight } from 'features/viewing/AcusoftViewer/AcusoftViewerControl/hooks/useScrollToHighlight';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { Allotment } from 'allotment';
import 'allotment/dist/style.css';
import { Folder, Close, AttachEmail, FilePresent } from '@mui/icons-material';
import DocumentSearch from '../DocumentSearch';

const mapping = {
  trialbook: 'trialbundles',
  'court-bundle': 'court-bundles',
  bundle: 'bundles',
  'team-bundle': 'teamBundles',
  'private-bundle': 'privateBundles',
};

const smallViewerScreenWidth = 1440;
const compactViewerScreenWidth = 1024;
const ATTACHMENT_NAME_MAX_LENGTH = 50;

export const BundleLocations = ({
  bundleLocations,
  fileId,
  filterVal,
  filePageCount = 0,
  isCrossRef = false,
  isGrid = false,
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [foundMatch, setFoundMatch] = useState(false);
  const [allBundleLocations, setAllBundleLocations] = useState([]);
  const currentCaseId = useSelector(selectCurrentCaseId);
  const isBundleTabPage = useSelector(selectBundleTabPage);
  const [folderSpecificFileDetails, setFolderSpeficFileDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const isDropDownOpen = Boolean(anchorEl);
  const locationsList = isGrid ? allBundleLocations : bundleLocations;
  const dropDownItemClicked = (path, containerType, folderFileIdOverride) => {
    const targetFileId = folderFileIdOverride || fileId;
    history.push(
      history.location.pathname.replace(/\/case.*/, ``) +
        `/case/${currentCaseId}/${mapping[containerType]}/${path}#${targetFileId}`,
    );
  };

  const getFolderSpecificDetails = useCallback(() => {
    dispatch(fetchFolderSpecificFileDetails({ fileId, firstTrialbookOnly: false }))
      .then((res) => {
        setFolderSpeficFileDetails(res);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  }, [dispatch, fileId]);

  const getFileDetails = useCallback(() => {
    setFetching(true);
    dispatch(fetchFileDetails({ fileId })).then((res) => {
      setAllBundleLocations([...res.bundleLocations]);
      setFetching(false);
      setTimeout(() => getFolderSpecificDetails(), 0);
    });
  }, [fileId, dispatch, getFolderSpecificDetails]);

  useEffect(() => {
    if (isDropDownOpen) {
      setLoading(true);
      if (isGrid) {
        getFileDetails();
      } else {
        getFolderSpecificDetails();
      }
    }
  }, [isDropDownOpen, isGrid, getFolderSpecificDetails, getFileDetails]);

  useEffect(() => {
    if (filterVal) {
      const fileNames = locationsList.map((item) => item.fileName.toLowerCase()).join(', ');
      if (filterVal && fileNames.includes(filterVal.toLowerCase())) {
        setFoundMatch(true);
      } else {
        setFoundMatch(false);
      }
    }
  }, [filterVal, locationsList]);

  return (
    <>
      <IconButton
        size="small"
        disableRipple
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        sx={{ p: 0, m: 0 }}
      >
        <Folder
          color={foundMatch ? 'primary' : 'action'}
          fontSize="small"
          sx={{ p: 0, m: 0, fontSize: '1.15rem', ...(isCrossRef && { color: 'white' }) }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isDropDownOpen}
        onClose={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAnchorEl(null);
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
      >
        <MenuItem disabled>
          {isCrossRef
            ? T.translate('viewing.crossRefLocations')
            : T.translate('viewing.fileLocations')}
        </MenuItem>
        {fetching ? (
          <MenuItem>
            <CircularProgress size={'2rem'} />
          </MenuItem>
        ) : locationsList.length > 0 ? (
          locationsList.map((item, idx) => {
            const details = folderSpecificFileDetails.find((val) => val.folderId === item.folderId);
            return (
              <MenuItem
                key={idx}
                onClick={() =>
                  dropDownItemClicked(item.path, item.containerType, item.folderFileId)
                }
              >
                {`${mapping[item.containerType]} > ${item.pathName} > `}
                <span
                  dangerouslySetInnerHTML={{
                    __html: getHighlightedText(item.fileName, filterVal),
                  }}
                ></span>
                {loading ? (
                  <CircularProgress size={'1rem'} sx={{ marginLeft: '1rem' }} />
                ) : details && details.globalPagePrefix ? (
                  <span style={{ whiteSpace: 'pre' }}>
                    {isBundleTabPage && details.tab ? (
                      <>{`  [${details.globalPagePrefix} / ${details.tab}]`}</>
                    ) : (
                      <>
                        {details.tab ? ` [Tab ${details.tab}]` : ''} {details.globalPagePrefix}
                        {details.startPage} -
                        {parseInt(details.startPage, 10) + parseInt(filePageCount, 10) - 1}
                      </>
                    )}
                  </span>
                ) : (
                  ''
                )}
              </MenuItem>
            );
          })
        ) : (
          <MenuItem>{T.translate('viewing.noFileLocations')}</MenuItem>
        )}
      </Menu>
    </>
  );
};

BundleLocations.propTypes = {
  bundleLocations: PropTypes.array.isRequired,
  fileId: PropTypes.string.isRequired,
  filterVal: PropTypes.string,
  filePageCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isCrossRef: PropTypes.bool,
  isGrid: PropTypes.bool,
};

const AttachmentIndicators = ({ attachments = [], attachedToDocuments = [], onSelectDocument }) => {
  const [attachmentsMenuAnchorEl, setAttachmentsMenuAnchorEl] = useState(null);
  const [attachedToMenuAnchorEl, setAttachedToMenuAnchorEl] = useState(null);
  const hasAttachments = Array.isArray(attachments) && attachments.length > 0;
  const hasAttachedTo = Array.isArray(attachedToDocuments) && attachedToDocuments.length > 0;
  const attachmentsMenuOpen = Boolean(attachmentsMenuAnchorEl);
  const attachedToMenuOpen = Boolean(attachedToMenuAnchorEl);
  const attachmentsHeadingLabel = T.translate('case.fileAttachments');
  const attachedToHeadingLabel = T.translate('generic.parentEmail');
  const attachmentsIdLabel = T.translate('generic.id');
  const attachmentsNameLabel = T.translate('generic.name');

  if (!hasAttachments && !hasAttachedTo) {
    return null;
  }

  const handleAttachmentsMenuClose = () => setAttachmentsMenuAnchorEl(null);
  const handleAttachedToMenuClose = () => setAttachedToMenuAnchorEl(null);
  const handleSelectDocument = (targetDocument, closeMenu, source) => {
    closeMenu();
    if (!targetDocument?.id && !targetDocument?.fileId) return;
    if (typeof onSelectDocument === 'function') {
      onSelectDocument(targetDocument, source);
    }
    if (typeof window !== 'undefined' && typeof window.document !== 'undefined') {
      const activeElement = window.document.activeElement;
      if (activeElement && typeof activeElement.blur === 'function') {
        activeElement.blur();
      }
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '0.35rem' }}>
        {hasAttachments && (
          <Tooltip title={T.translate('generic.attachments')} enterDelay={0} leaveDelay={0} arrow>
            <IconButton
              id="viewer-attachments-menu-button"
              size="small"
              aria-haspopup="true"
              aria-expanded={attachmentsMenuOpen ? 'true' : undefined}
              aria-controls={attachmentsMenuOpen ? 'viewer-attachments-menu' : undefined}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (attachmentsMenuOpen) {
                  handleAttachmentsMenuClose();
                } else {
                  setAttachedToMenuAnchorEl(null);
                  setAttachmentsMenuAnchorEl(event.currentTarget);
                }
              }}
              sx={{
                color: '#4567e5',
                fontSize: '18px',
                marginLeft: '0.15rem',
                '&:hover': {
                  backgroundColor: 'rgba(69, 103, 229, 0)',
                },
              }}
            >
              <AttachEmail fontSize="medium" />
            </IconButton>
          </Tooltip>
        )}
        {hasAttachedTo && (
          <Tooltip title={attachedToHeadingLabel} enterDelay={0} leaveDelay={0} arrow>
            <IconButton
              id="viewer-attached-to-menu-button"
              size="small"
              aria-haspopup="true"
              aria-expanded={attachedToMenuOpen ? 'true' : undefined}
              aria-controls={attachedToMenuOpen ? 'viewer-attached-to-menu' : undefined}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (attachedToMenuOpen) {
                  handleAttachedToMenuClose();
                } else {
                  setAttachmentsMenuAnchorEl(null);
                  setAttachedToMenuAnchorEl(event.currentTarget);
                }
              }}
              sx={{
                marginLeft: '0.15rem',
                '&:hover': {
                  backgroundColor: 'rgba(69, 103, 229, 0)',
                },
              }}
            >
              <FilePresent fontSize="medium" />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <Menu
        id="viewer-attachments-menu"
        anchorEl={attachmentsMenuAnchorEl}
        open={attachmentsMenuOpen}
        onClose={handleAttachmentsMenuClose}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        MenuListProps={{
          dense: true,
        }}
        disableScrollLock
        PaperProps={{
          sx: {
            maxHeight: 350,
            minWidth: 220,
            py: 0,
            border: '1px solid #cfcfcf',
          },
        }}
      >
        <div
          style={{
            padding: '15px 10px',
            fontWeight: 600,
            fontSize: '14px',
            color: '#5c5c5c',
          }}
        >
          {attachmentsHeadingLabel}
        </div>
        {attachments.map((attachment, index) => {
          const attachmentId = attachment?.id || '-';
          const rawName = typeof attachment?.name === 'string' ? attachment.name.trim() : '';
          const shouldTruncateName = rawName.length > ATTACHMENT_NAME_MAX_LENGTH;
          const truncatedName = shouldTruncateName
            ? rawName.slice(0, ATTACHMENT_NAME_MAX_LENGTH)
            : rawName;
          return (
            <MenuItem
              key={attachment?.id || `${attachment?.name || 'attachment'}-${index}`}
              onClick={() => handleSelectDocument(attachment, handleAttachmentsMenuClose, 'attachments')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                padding: '8px',
              }}
            >
              <span style={{ fontWeight: 600, fontSize: '12px' }}>
                {`${attachmentsIdLabel}: ${attachmentId}`}
              </span>
              <Tooltip title={shouldTruncateName ? rawName : ''} arrow placement="top">
                <span
                  style={{
                    color: '#4a4a4a',
                    display: 'flex',
                    fontSize: '12px',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {`${attachmentsNameLabel}: ${truncatedName || '-'}`}
                  {shouldTruncateName && (
                    <span
                      style={{
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      ...
                    </span>
                  )}
                </span>
              </Tooltip>
            </MenuItem>
          );
        })}
      </Menu>
      <Menu
        id="viewer-attached-to-menu"
        anchorEl={attachedToMenuAnchorEl}
        open={attachedToMenuOpen}
        onClose={handleAttachedToMenuClose}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        MenuListProps={{
          dense: true,
        }}
        disableScrollLock
        PaperProps={{
          sx: {
            maxHeight: 350,
            minWidth: 220,
            py: 0,
            border: '1px solid #cfcfcf',
          },
        }}
      >
        <div
          style={{
            padding: '15px 10px',
            fontWeight: 600,
            fontSize: '14px',
            color: '#5c5c5c',
          }}
        >
          {attachedToHeadingLabel}
        </div>
        {attachedToDocuments.map((attachment, index) => {
          const attachmentId = attachment?.id || '-';
          const rawName = typeof attachment?.name === 'string' ? attachment.name.trim() : '';
          const shouldTruncateName = rawName.length > ATTACHMENT_NAME_MAX_LENGTH;
          const truncatedName = shouldTruncateName
            ? rawName.slice(0, ATTACHMENT_NAME_MAX_LENGTH)
            : rawName;
          return (
            <MenuItem
              key={attachment?.id || `${attachment?.name || 'attached-to'}-${index}`}
              onClick={() =>
                handleSelectDocument(attachment, handleAttachedToMenuClose, 'isAttachedTo')
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                padding: '8px',
              }}
            >
              <span style={{ fontWeight: 600, fontSize: '12px' }}>
                {`${attachmentsIdLabel}: ${attachmentId}`}
              </span>
              <Tooltip title={shouldTruncateName ? rawName : ''} arrow placement="top">
                <span
                  style={{
                    color: '#4a4a4a',
                    display: 'flex',
                    fontSize: '12px',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {`${attachmentsNameLabel}: ${truncatedName || '-'}`}
                  {shouldTruncateName && (
                    <span
                      style={{
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      ...
                    </span>
                  )}
                </span>
              </Tooltip>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

AttachmentIndicators.propTypes = {
  attachments: PropTypes.array,
  attachedToDocuments: PropTypes.array,
  onSelectDocument: PropTypes.func,
};

const RightSidebarViewerWithScroll = ({ fileData, viewerControl, gridView = false }) => {
  const [allHighlights] = useState([]);
  const { scrollSelectedHighlight, scrollToHighlight } = useScrollToHighlight({ viewerControl });

  return (
    <RightSidebarViewer
      selectedHighlight={scrollSelectedHighlight}
      highlights={allHighlights}
      scrollToHighlight={scrollToHighlight}
      fileData={fileData}
      viewerControl={viewerControl}
      gridView={gridView}
    />
  );
};

RightSidebarViewerWithScroll.propTypes = {
  fileData: PropTypes.any,
  viewerControl: PropTypes.any,
  gridView: PropTypes.bool,
};
export class Viewer extends Component {
  static propTypes = {
    caseId: PropTypes.string,
    fileId: PropTypes.string,
    folderId: PropTypes.string,
    folderFileId: PropTypes.string,
    startPage: PropTypes.string,
    tab: PropTypes.string,
    globalPagePrefix: PropTypes.string,
    goToPage: PropTypes.string,
    isBundleTabPage: PropTypes.bool,
    isConfidentialityRing: PropTypes.bool,
    folderSpecificFileDetails: PropTypes.array,
    containers: PropTypes.array,
    fetchCasesPending: PropTypes.bool,
    isRecordingsPage: PropTypes.bool,
    actions: PropTypes.object,
    actionsV: PropTypes.object,
    history: PropTypes.object,
    viewerControl: PropTypes.any,
    url: PropTypes.string,
    documents: PropTypes.array,
    docSearchVal: PropTypes.string,
    docSearchLocation: PropTypes.object,
    fileToCompare: PropTypes.object,
    docSearchValForCompare: PropTypes.string,
    docSearchLocationForCompare: PropTypes.object,
  };

  state = {
    title: document.title,
    searchDetails: null,
    searchFunctionsMain: null,
    searchFunctionsCompare: null,
    filePageCount: null,
    fileType: null,
    fileName: null,
    bundleLocations: [],
    bundleSpecificFileDetails: {},
    currentFileMetaData: null,
    folderFilesById: {},
    parentMostFolder: null,
    leafFoldersInParent: [],
    leafNavigationRange: null,
    attachmentIndicatorHintsByFileId: {},
  };

  folderFilesInFlight = {};
  folderFilesFetchKeyById = {};
  lastLocationSearch = '';
  lastNavigationIndex = null;
  leafPrefetchThreshold = 10;
  leafBufferInFlight = false;
  navInFlight = false;
  skipNextFolderSpecificFileDetails = false;
  keyboardFocusRef = React.createRef();

  focusViewerShellForKeyboardShortcuts = () => {
    const isJsDomEnvironment =
      typeof navigator !== 'undefined' &&
      typeof navigator.userAgent === 'string' &&
      navigator.userAgent.toLowerCase().includes('jsdom');
    try {
      if (
        !isJsDomEnvironment &&
        typeof window !== 'undefined' &&
        typeof window.focus === 'function'
      ) {
        window.focus();
      }
    } catch (_error) {
      // Ignore focus errors in non-browser environments (e.g. jsdom tests).
    }
    const focusTarget = this.keyboardFocusRef?.current;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch (_error) {
        focusTarget.focus();
      }
    }
  };

  getResolvedFileIdFromMeta = (meta) => {
    if (!meta) return null;
    return meta.fileId || meta.id || meta.file?.fileId || meta.file?.id || null;
  };

  getAttachmentRelationships = (meta) => {
    if (!meta) {
      return { attachments: null, attachedToDocuments: null };
    }
    const attachmentsCandidates = [
      meta.attachments,
      meta.file?.attachments,
      meta.fileData?.attachments,
      meta.document?.attachments,
    ];
    const attachedToCandidates = [
      meta.isAttachedTo,
      meta.file?.isAttachedTo,
      meta.fileData?.isAttachedTo,
      meta.document?.isAttachedTo,
    ];
    const attachments = attachmentsCandidates.find((candidate) => Array.isArray(candidate)) || null;
    const attachedToDocuments =
      attachedToCandidates.find((candidate) => Array.isArray(candidate)) || null;
    return { attachments, attachedToDocuments };
  };

  getCurrentlyPresentingDoc = () => {
    try {
      const caseId = this.props.case?.id;
      if (!caseId) return null;
      const raw = sessionStorage.getItem(`${caseId}.currentlyPresenting`);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  };

  fetchDetails = () => {
    const requestedFileId = this.props.fileId;
    const caseId = this.props.caseId || this.props.case?.id;
    return this.props.actions
      .fetchFileDetails({ fileId: requestedFileId, firstTrialBookOnly: true })
      .then((res) => {
        if (this.props.fileId !== requestedFileId) return;
        const { type: fileType, name: fileName, bundleLocations, pageCount: filePageCount } = res;

        const currentFileFirstTrialBundleLocation =
          bundleLocations &&
          bundleLocations.length > 0 &&
          bundleLocations.find((bundle) => bundle.containerType === 'trialbook');

        if (currentFileFirstTrialBundleLocation) {
          this.props.actions.setCurrentSelectedFile(res);

          // Skip if tabs API was already called during prev/next navigation
          if (this.skipNextFolderSpecificFileDetails) {
            this.skipNextFolderSpecificFileDetails = false;
          } else {
            this.props.actions.fetchFolderSpecificFileDetails({
              fileId: this.props.fileId,
              firstTrialbookOnly: false,
            });
          }
        }

        const resolvedFileId = this.getResolvedFileIdFromMeta(res) || requestedFileId || null;
        const { attachments, attachedToDocuments } = this.getAttachmentRelationships(res);
        const hasAttachmentRelationshipData =
          (Array.isArray(attachments) && attachments.length > 0) ||
          (Array.isArray(attachedToDocuments) && attachedToDocuments.length > 0);

        this.setState((prevState) => ({
          fileType,
          fileName,
          bundleLocations,
          filePageCount,
          currentFileMetaData: res,
          attachmentIndicatorHintsByFileId:
            resolvedFileId && hasAttachmentRelationshipData
              ? {
                  ...prevState.attachmentIndicatorHintsByFileId,
                  [resolvedFileId]: {
                    attachments: Array.isArray(attachments) ? attachments : [],
                    isAttachedTo: Array.isArray(attachedToDocuments) ? attachedToDocuments : [],
                  },
                }
              : prevState.attachmentIndicatorHintsByFileId,
        }));

        if (!caseId || !resolvedFileId) return;
        api
          .get(`/cases/${caseId}/files/${resolvedFileId}/details?includeAttachments=true`)
          .then((fullDetailsRes) => {
            if (this.props.fileId !== requestedFileId) return;
            if (!fullDetailsRes || fullDetailsRes.error) return;
            const { attachments: fullAttachments, attachedToDocuments: fullAttachedTo } =
              this.getAttachmentRelationships(fullDetailsRes);
            if (!Array.isArray(fullAttachments) && !Array.isArray(fullAttachedTo)) return;

            this.setState((prevState) => {
              const prevCurrentMeta = prevState.currentFileMetaData;
              const prevMetaId = this.getResolvedFileIdFromMeta(prevCurrentMeta);
              const shouldMergeCurrentMeta =
                prevCurrentMeta &&
                (String(prevMetaId || '') === String(resolvedFileId) ||
                  String(this.props.fileId || '') === String(resolvedFileId));
              const mergedCurrentMeta = shouldMergeCurrentMeta
                ? {
                    ...prevCurrentMeta,
                    ...(Array.isArray(fullAttachments) ? { attachments: fullAttachments } : {}),
                    ...(Array.isArray(fullAttachedTo) ? { isAttachedTo: fullAttachedTo } : {}),
                  }
                : prevCurrentMeta;
              return {
                currentFileMetaData: mergedCurrentMeta,
                attachmentIndicatorHintsByFileId: {
                  ...prevState.attachmentIndicatorHintsByFileId,
                  [resolvedFileId]: {
                    attachments: Array.isArray(fullAttachments) ? fullAttachments : [],
                    isAttachedTo: Array.isArray(fullAttachedTo) ? fullAttachedTo : [],
                  },
                },
              };
            });
          })
          .catch(() => {});
      })
      .catch(({ error }) => {
        Logger.ERROR('Fetching File Details', error);
      });
  };

  componentDidMount() {
    // if (this.props.goToPage) {
    //   this.props.actionsV.setGoToPageNumber(parseInt(this.props.goToPage));
    // }

    if (
      this.props.actions?.fetchContainers &&
      (!this.props.containers || !this.props.containers.length)
    ) {
      this.props.actions.fetchContainers();
    }
    if (this.props.fileId) this.fetchDetails();
    this.maybeFetchFolderFiles();
    this.resolveLeafFoldersForCurrentFolder();
    this.lastLocationSearch = history.location?.search || '';
    window.addEventListener('keydown', this.handleBackspaceNavigateBack, true);
    document.addEventListener('keydown', this.handleBackspaceNavigateBack, true);
    this.focusViewerShellForKeyboardShortcuts();

    document.title = this.state.title;
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleBackspaceNavigateBack, true);
    document.removeEventListener('keydown', this.handleBackspaceNavigateBack, true);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.fileId !== this.props.fileId) {
      this.fetchDetails();
      setTimeout(() => this.focusViewerShellForKeyboardShortcuts(), 0);
    }

    // Handle when folderSpecificFileDetails are updated in Redux
    if (
      prevProps.folderSpecificFileDetails !== this.props.folderSpecificFileDetails &&
      this.props.folderSpecificFileDetails &&
      this.props.folderSpecificFileDetails.length > 0
    ) {
      // Find the current bundle location
      const currentFileFirstTrialBundleLocation =
        this.state.bundleLocations &&
        this.state.bundleLocations.length > 0 &&
        this.state.bundleLocations.find((bundle) => bundle.containerType === 'trialbook');

      if (currentFileFirstTrialBundleLocation) {
        // Find the matching bundle details
        const bundleDetails = this.props.folderSpecificFileDetails.find(
          (item) => item.folderId === currentFileFirstTrialBundleLocation.folderId,
        );

        // Update state if bundle details found
        if (bundleDetails) {
          this.setState({ bundleSpecificFileDetails: bundleDetails });
        }
      }
    }

    this.maybeFetchFolderFiles();

    const currentSearch = history.location?.search || '';
    const searchChanged = currentSearch !== this.lastLocationSearch;
    if (searchChanged) {
      this.lastLocationSearch = currentSearch;
    }
    if (
      searchChanged ||
      prevProps.folderId !== this.props.folderId ||
      prevProps.containers !== this.props.containers
    ) {
      this.resolveLeafFoldersForCurrentFolder();
    }

    const navigationChanged =
      prevProps.fileId !== this.props.fileId ||
      prevProps.folderFileId !== this.props.folderFileId ||
      prevState.leafNavigationRange !== this.state.leafNavigationRange ||
      prevState.leafFoldersInParent !== this.state.leafFoldersInParent;
    if (navigationChanged) {
      this.maybePrefetchLeafFolder(this.getPrevNavigationFileId(prevProps));
    }
  }

  getMatchingFolderFromQueryParams = () => {
    const queryParam = Object.fromEntries(new URLSearchParams(window.location.search));
    const folderSpecificFileDetails = this.props.folderSpecificFileDetails;

    // Check if all three required parameters exist in queryParam
    const hasRequiredQueryParams = ['tab', 'globalPagePrefix', 'pageCount'].every((param) =>
      Object.keys(queryParam).includes(param),
    );

    // If any parameter is missing, return null
    if (!hasRequiredQueryParams) {
      return null;
    }

    // If folderSpecificFileDetails isn't available or is empty, return null
    if (!folderSpecificFileDetails || folderSpecificFileDetails.length === 0) {
      return null;
    }

    // Find and return the matching folder or null if no match
    return (
      folderSpecificFileDetails.find(
        (folder) =>
          folder.tab === queryParam.tab &&
          folder.globalPagePrefix.toUpperCase() === queryParam.globalPagePrefix.toUpperCase() &&
          folder.pageCount === queryParam.pageCount,
      ) || null
    );
  };

  isQueryParamMatchingAnyFolder = () => {
    return this.getMatchingFolderFromQueryParams() !== null;
  };

  handleBackspaceNavigateBack = (event) => {
    if (!event) return;
    const isBackspaceKey =
      event.key === 'Backspace' || event.code === 'Backspace' || event.keyCode === 8;
    if (!isBackspaceKey) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const targetElement = event.target;
    const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
    const elementIsEditable = (element) => {
      if (!element) return false;
      const tagName = element.tagName?.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea') {
        return !(element.readOnly || element.disabled);
      }
      if (tagName === 'select') return !element.disabled;
      if (element.isContentEditable || element.getAttribute?.('contenteditable') === 'true')
        return true;
      return false;
    };
    if (elementIsEditable(targetElement) || elementIsEditable(activeElement)) return;
    const currentPath = history.location?.pathname || '';
    const isFullFileRoute = /\/case\/[^/]+\/(?:present-view\/)?files\/[^/]+\/?$/.test(currentPath);
    if (!isFullFileRoute) return;
    event.preventDefault();
    history.goBack();
  };

  getUrlBrandTextOverrides = () => {
    if (typeof window === 'undefined') return null;
    const currentPath = history.location?.pathname || window.location?.pathname || '';
    const currentSearch = history.location?.search || window.location?.search || '';
    const searchParams = new URLSearchParams(currentSearch);
    const folderIdFromSearch = searchParams.get('folderId');
    const isBigViewScreen =
      !!folderIdFromSearch &&
      folderIdFromSearch !== 'recordings' &&
      /^\/case\/[^/]+\/files\/[^/]+$/.test(currentPath) &&
      searchParams.get('view') !== 'share';
    if (!isBigViewScreen) return null;
    return {
      tab: searchParams.get('tab'),
      startPage: searchParams.get('startPage'),
      globalPagePrefix: searchParams.get('globalPagePrefix'),
      pageCount: searchParams.get('pageCount'),
      goToPage: searchParams.get('goToPage'),
    };
  };

  getFormattedPageRange = (overrides = null) => {
    // Determine where to get the values from based on isQueryParamMatchingAnyFolder
    let startPage, globalPagePrefix, filePageCount;

    if (!this.isQueryParamMatchingAnyFolder()) {
      // Use values from bundleSpecificFileDetails
      startPage = this.state.bundleSpecificFileDetails.startPage || this.props.startPage;
      globalPagePrefix =
        this.state.bundleSpecificFileDetails.globalPagePrefix || this.props.globalPagePrefix;
      filePageCount = this.state.bundleSpecificFileDetails.pageCount || this.state.filePageCount;
    } else {
      // Use values from props
      startPage = this.props.startPage;
      globalPagePrefix = this.props.globalPagePrefix.toUpperCase();
      filePageCount = this.state.filePageCount;
    }

    const hasOverride = (value) => value !== null && value !== undefined && value !== '';
    if (overrides) {
      if (hasOverride(overrides.startPage)) startPage = overrides.startPage;
      if (hasOverride(overrides.globalPagePrefix))
        globalPagePrefix = overrides.globalPagePrefix.toUpperCase();
      if (hasOverride(overrides.pageCount)) filePageCount = overrides.pageCount;
    }

    // Return early if required values are missing
    if (!startPage || !globalPagePrefix || !filePageCount) {
      return '';
    }

    // Calculate end page based on whether startPage contains a decimal
    let endPage;

    if (startPage?.includes('.')) {
      const fileStartPage = startPage.split('.');
      const baseNumber = fileStartPage[0];
      let decimalPart = fileStartPage[1];

      const isStartingWithZero = decimalPart.startsWith('0');

      // Handle decimal part formatting
      let decimalValue;
      if (isStartingWithZero) {
        // Remove leading zero but preserve it in the output
        decimalValue = parseInt(decimalPart.slice(1), 10);
        endPage = `${baseNumber}.0${decimalValue + parseInt(filePageCount, 10) - 1}`;
      } else {
        decimalValue = parseInt(decimalPart, 10);
        endPage = `${baseNumber}.${decimalValue + parseInt(filePageCount, 10) - 1}`;
      }
    } else {
      // Simple integer case
      endPage = parseInt(startPage, 10) + parseInt(filePageCount, 10) - 1;
    }

    return ` ${globalPagePrefix}${startPage}-${endPage}`;
  };

  getBrandText = (isSharing) => {
    const urlOverrides = this.getUrlBrandTextOverrides();
    const hasUrlValue = (value) => value !== null && value !== undefined && value !== '';
    const urlTab = hasUrlValue(urlOverrides?.tab) ? urlOverrides.tab : null;
    const urlStartPage = hasUrlValue(urlOverrides?.startPage) ? urlOverrides.startPage : null;
    const urlGlobalPagePrefix = hasUrlValue(urlOverrides?.globalPagePrefix)
      ? urlOverrides.globalPagePrefix
      : null;
    const urlPageCount = hasUrlValue(urlOverrides?.pageCount) ? urlOverrides.pageCount : null;
    const urlGoToPage = hasUrlValue(urlOverrides?.goToPage) ? urlOverrides.goToPage : null;

    const folder = this.state.bundleLocations.find(
      (bundle) => bundle.folderId === this.props.folderId,
    );

    const brandText = this.props.isRecordingsPage
      ? 'Recordings > '
      : folder
        ? `${`${mapping[folder.containerType]} > ${folder.pathName}`} > ${folder.fileName}`
        : this.state.fileName
          ? this.state.fileName
          : '';

    const getPageRange = () => {
      if (this.props.isBundleTabPage) {
        if (urlGlobalPagePrefix) {
          return ` ${urlGlobalPagePrefix.toUpperCase()} / `;
        }
        if (!this.isQueryParamMatchingAnyFolder()) {
          // Make sure bundleSpecificFileDetails exists and has globalPagePrefix
          if (
            this.state.bundleSpecificFileDetails &&
            this.state.bundleSpecificFileDetails.globalPagePrefix
          ) {
            // Add a space after the slash to match expected format "A / 5"
            return ` ${this.state.bundleSpecificFileDetails.globalPagePrefix} / `;
          }
          // If bundleSpecificFileDetails is not available, fall back to props
          if (this.props.globalPagePrefix) {
            return ` ${this.props.globalPagePrefix} / `;
          }
          return ''; // No global page prefix available
        }
        // Fix this line to match expected format "A / 5" by adding space after the slash
        return this.props.globalPagePrefix
          ? ` ${this.props.globalPagePrefix.toUpperCase()} / `
          : '';
      }
      // For the non-bundle case, make sure we have all needed values before calculating
      if (
        (urlStartPage || urlGoToPage || this.props.startPage || this.props.goToPage) &&
        (urlPageCount ||
          this.state.filePageCount ||
          (this.state.bundleSpecificFileDetails && this.state.bundleSpecificFileDetails.pageCount))
      ) {
        return this.getFormattedPageRange({
          startPage: urlStartPage,
          globalPagePrefix: urlGlobalPagePrefix,
          pageCount: urlPageCount,
        });
      }

      return ''; // Return empty string if we don't have enough data yet
    };

    const getFileId = () => (this.props.fileId ? ` - (${this.props.fileId})` : '');

    const getTab = () => {
      // Always use props.tab as a fallback
      const tab = urlTab
        ? urlTab
        : !this.isQueryParamMatchingAnyFolder()
          ? this.state.bundleSpecificFileDetails.tab || this.props.tab
          : this.props.tab;
      if (this.props.isBundleTabPage) {
        const hasGlobalPrefix = Boolean(
          (
            urlGlobalPagePrefix ||
            (!this.isQueryParamMatchingAnyFolder()
              ? this.state.bundleSpecificFileDetails.globalPagePrefix || this.props.globalPagePrefix
              : this.props.globalPagePrefix)
          )?.trim(),
        );

        if (!hasGlobalPrefix) return '';

        return tab ? ` ${tab}` : '';
      }
      return tab ? ` ${tab}` : '';
    };

    const getGlobalPagePrefix = () => {
      // Retrieve globalPagePrefix from bundleSpecificFileDetails if available, otherwise use props.globalPagePrefix
      const gpPrefix =
        urlGlobalPagePrefix ||
        (!this.isQueryParamMatchingAnyFolder()
          ? this.state.bundleSpecificFileDetails.globalPagePrefix || this.props.globalPagePrefix
          : this.props.globalPagePrefix) ||
        '';

      if (this.props.isBundleTabPage) {
        return gpPrefix;
      }
      return gpPrefix ? ` [Tab ${gpPrefix}]` : '';
    };

    if (isSharing) {
      const presentingDoc = this.getCurrentlyPresentingDoc();
      const presentingPrefix =
        presentingDoc?.globalPagePrefix ||
        presentingDoc?.bundleLocations?.[0]?.globalPagePrefix ||
        '';
      const presentingTab = presentingDoc?.tab ?? presentingDoc?.bundleLocations?.[0]?.tab ?? '';
      const hasPresentingDoc = !!presentingDoc;
      const tabText = this.props.isBundleTabPage
        ? `${hasPresentingDoc ? presentingTab || '' : getTab()}`
        : '';
      if (this.props.isBundleTabPage) {
        const globalPrefix = hasPresentingDoc
          ? presentingPrefix || ''
          : getGlobalPagePrefix() || this.state?.bundleLocations?.[0]?.globalPagePrefix || '';
        const tabLabel = tabText?.toString().trim();
        const globalTabLabel = globalPrefix
          ? `[${globalPrefix}${tabLabel ? `/${tabLabel}` : ''}] `
          : '';
        return `${globalTabLabel}${this.props.fileId} ${presentingDoc?.name || this.state?.bundleLocations?.[0]?.fileName || folder?.fileName || this.state.fileName || ''}`;
      }

      return `${this.props.fileId} ${tabText} ${presentingDoc?.name || folder?.fileName || this.state.bundleLocations?.[0]?.fileName || this.state.fileName || ''}`;
    }

    return `${brandText}${getFileId()}${getPageRange()}${this.props.isBundleTabPage ? getTab() : ''}`;
  };

  searchHandler = (searchDetails) => {
    this.setState({ searchDetails });
  };

  searchFunctionsHandlerMain = (searchFunctions) => {
    this.setState({ searchFunctionsMain: searchFunctions });
  };

  searchFunctionsHandlerCompare = (searchFunctions) => {
    this.setState({ searchFunctionsCompare: searchFunctions });
  };

  normalizeFileMeta = (meta) => {
    if (!meta) return meta;
    const bl = (meta.bundleLocations && meta.bundleLocations[0]) || {};
    const normalized = {
      ...meta,
      globalPagePrefix: meta.globalPagePrefix || bl.globalPagePrefix || undefined,
      tab: meta.tab || bl.tab || undefined,
      startPage: meta.startPage ?? bl.startPage ?? undefined,
      folderFileId: meta.folderFileId || bl.folderFileId || undefined,
      pageCount: meta.pageCount || bl.pageCount || meta.pageCount,
    };
    return normalized;
  };

  getFolderIdFromSearch = () => {
    const currentSearch = history.location?.search || '';
    const searchParams = new URLSearchParams(currentSearch);
    return searchParams.get('folderId');
  };

  getFolderFileIdFromSearch = () => {
    const currentSearch = history.location?.search || '';
    const searchParams = new URLSearchParams(currentSearch);
    return searchParams.get('folderFileId');
  };

  getCurrentNavigationFileId = () =>
    this.getFolderFileIdFromSearch() || this.props.folderFileId || this.props.fileId || null;

  getPrevNavigationFileId = (prevProps) => prevProps?.folderFileId || prevProps?.fileId || null;

  isExactFolderFileUrl = () => {
    const currentPath = history.location?.pathname || '';
    if (!/^\/case\/[^/]+\/files\/[^/]+$/.test(currentPath)) return false;
    const currentSearch = history.location?.search || '';
    const searchParams = new URLSearchParams(currentSearch);
    const folderId = searchParams.get('folderId');
    if (!folderId) return false;
    if (this.props.folderId && folderId !== this.props.folderId) return false;
    return Array.from(searchParams.keys()).length === 1;
  };

  getCachedFolderFiles = (folderId) => {
    if (!folderId) return null;
    const { folderFilesById } = this.state;
    if (!Object.prototype.hasOwnProperty.call(folderFilesById, folderId)) return null;
    return folderFilesById[folderId];
  };

  getStoredFolderFiles = (folderId) => {
    const { documents } = this.props;
    if (!folderId || !Array.isArray(documents) || documents.length === 0) return null;
    const matching = documents.filter((doc) => doc?.folderId === folderId);
    return matching.length > 0 ? matching : null;
  };

  getFolderFilesForNavigation = () => {
    const folderId = this.getFolderIdFromSearch() || this.props.folderId;
    if (!folderId) return [];
    const leafNavigationFiles = this.getLeafNavigationFiles();
    if (leafNavigationFiles !== null) {
      return leafNavigationFiles;
    }
    const cachedFiles = this.getCachedFolderFiles(folderId);
    if (cachedFiles !== null) {
      return cachedFiles;
    }
    if (!this.isExactFolderFileUrl()) return [];
    const storedFiles = this.getStoredFolderFiles(folderId) || [];
    return storedFiles;
  };

  getFolderFileId = (file) => {
    if (!file) return null;
    return file.id || file.file?.id || file.fileId || file.folderFileId || null;
  };

  getNavigationFileName = (file) => {
    if (!file) return '';
    return (
      file.name ||
      file.fileName ||
      file.filename ||
      file.originalName ||
      file.file?.name ||
      file.file?.fileName ||
      file.file?.filename ||
      ''
    );
  };

  getNavigationTooltipTitle = (file, fallbackId, labelKey) => {
    const fileId = this.getFolderFileId(file) || fallbackId;
    const fileName = this.getNavigationFileName(file);
    if (!fileId && !fileName) return null;
    const label = T.translate(labelKey);
    if (fileName && fileId) return `${label}: ${fileName} /${fileId}`;
    if (fileName) return `${label}: ${fileName}`;
    if (fileId) return `${label}: ${fileId}`;
    return label;
  };

  getFolderFileIdForUrl = (file) => {
    if (!file) return null;
    const folderFileId = file.folderFileId || file.file?.folderFileId || null;
    if (folderFileId) return folderFileId;
    return file.id || file.file?.id || file.fileId || null;
  };

  getNavigationFileMeta = (targetFileId, targetFolderFileId) => {
    const navigationFiles = this.getFolderFilesForNavigation();
    if (!Array.isArray(navigationFiles) || navigationFiles.length === 0) return null;
    if (targetFolderFileId) {
      const matchedByFolderFileId =
        navigationFiles.find((file) => file?.folderFileId === targetFolderFileId) || null;
      if (matchedByFolderFileId) return matchedByFolderFileId;
    }
    return (
      navigationFiles.find(
        (file) =>
          file?.id === targetFileId ||
          file?.file?.id === targetFileId ||
          file?.fileId === targetFileId,
      ) || null
    );
  };

  getLeafNavigationFilesForRange = (range, overrides = null) => {
    const { leafFoldersInParent } = this.state;
    if (!Array.isArray(leafFoldersInParent) || leafFoldersInParent.length <= 1) return null;
    if (!range) return null;
    const { startIndex, endIndex } = range;
    if (startIndex < 0 || endIndex < startIndex) return null;
    const files = [];
    for (let idx = startIndex; idx <= endIndex; idx += 1) {
      const folder = leafFoldersInParent[idx];
      const folderId = folder?.id;
      if (!folderId) continue;
      const hasOverride = overrides && Object.prototype.hasOwnProperty.call(overrides, folderId);
      const overrideFiles = hasOverride ? overrides[folderId] : null;
      const cachedFiles = hasOverride ? overrideFiles : this.getCachedFolderFiles(folderId);
      const folderFiles =
        cachedFiles !== null ? cachedFiles : this.getStoredFolderFiles(folderId) || [];
      if (Array.isArray(folderFiles) && folderFiles.length > 0) {
        files.push(...folderFiles);
      }
    }
    return files;
  };

  getLeafNavigationFiles = () => {
    return this.getLeafNavigationFilesForRange(this.state.leafNavigationRange);
  };

  getNavigationIndexForFile = (folderFiles, fileId) => {
    if (!fileId || !Array.isArray(folderFiles) || folderFiles.length === 0) return -1;
    return folderFiles.findIndex(
      (file) =>
        file?.id === fileId ||
        file?.folderFileId === fileId ||
        file?.file?.id === fileId ||
        file?.fileId === fileId,
    );
  };

  getNavigationIndexForCurrentFile = (folderFiles) => {
    if (!Array.isArray(folderFiles) || folderFiles.length === 0) return -1;
    const currentFolderFileId = this.getFolderFileIdFromSearch() || this.props.folderFileId || null;
    if (currentFolderFileId) {
      const index = this.getNavigationIndexForFile(folderFiles, currentFolderFileId);
      if (index !== -1) return index;
    }
    const currentFileId = this.props.fileId || null;
    if (currentFileId) {
      return this.getNavigationIndexForFile(folderFiles, currentFileId);
    }
    return -1;
  };

  isLeafNavigationActive = () => {
    const { leafFoldersInParent, leafNavigationRange } = this.state;
    return (
      Array.isArray(leafFoldersInParent) && leafFoldersInParent.length > 1 && !!leafNavigationRange
    );
  };

  extendLeafNavigationRange = (direction, targetIndex) => {
    this.setState((prevState) => {
      const range = prevState.leafNavigationRange;
      if (!range) return null;
      if (direction === 'next') {
        if (targetIndex <= range.endIndex) return null;
        return { leafNavigationRange: { ...range, endIndex: targetIndex } };
      }
      if (direction === 'prev') {
        if (targetIndex >= range.startIndex) return null;
        return { leafNavigationRange: { ...range, startIndex: targetIndex } };
      }
      return null;
    });
  };

  maybePrefetchLeafFolder = async () => {
    if (!this.isLeafNavigationActive() || this.leafBufferInFlight) return;
    this.leafBufferInFlight = true;
    try {
      const baseRange = this.state.leafNavigationRange;
      if (!baseRange) return;
      const localFilesById = {};
      let workingRange = { ...baseRange };
      workingRange = await this.ensureLeafNavigationBuffer('next', workingRange, localFilesById);
      workingRange = await this.ensureLeafNavigationBuffer('prev', workingRange, localFilesById);
      if (
        workingRange.startIndex !== baseRange.startIndex ||
        workingRange.endIndex !== baseRange.endIndex
      ) {
        this.setState({ leafNavigationRange: workingRange });
      }
    } finally {
      this.leafBufferInFlight = false;
    }
  };

  ensureLeafNavigationBuffer = async (direction, workingRange, localFilesById) => {
    if (!this.isLeafNavigationActive()) return;
    const { leafFoldersInParent, leafNavigationRange } = this.state;
    if (!leafNavigationRange || !workingRange) return workingRange;
    while (true) {
      const navigationFiles = this.getLeafNavigationFilesForRange(workingRange, localFilesById);
      if (!Array.isArray(navigationFiles) || navigationFiles.length === 0) break;
      const currentIndex = this.getNavigationIndexForCurrentFile(navigationFiles);
      if (currentIndex === -1) break;
      const remaining =
        direction === 'next' ? navigationFiles.length - currentIndex - 1 : currentIndex;
      if (remaining >= this.leafPrefetchThreshold) break;
      const targetIndex =
        direction === 'next' ? workingRange.endIndex + 1 : workingRange.startIndex - 1;
      if (targetIndex < 0 || targetIndex >= leafFoldersInParent.length) break;
      const targetFolderId = leafFoldersInParent[targetIndex]?.id;
      if (!targetFolderId || targetFolderId === 'recordings') {
        if (direction === 'next') {
          workingRange = { ...workingRange, endIndex: targetIndex };
        } else {
          workingRange = { ...workingRange, startIndex: targetIndex };
        }
        continue;
      }
      const cachedFiles = this.getCachedFolderFiles(targetFolderId);
      const storedFiles = this.getStoredFolderFiles(targetFolderId);
      const hasFiles =
        cachedFiles !== null
          ? Array.isArray(cachedFiles)
          : !!(storedFiles && storedFiles.length > 0);
      if (!hasFiles && !this.folderFilesInFlight[targetFolderId]) {
        const files = await this.fetchFolderFiles(
          targetFolderId,
          `leaf-buffer:${direction}:${targetFolderId}`,
        );
        if (Array.isArray(files)) {
          localFilesById[targetFolderId] = files;
        }
      }
      if (direction === 'next') {
        workingRange = { ...workingRange, endIndex: targetIndex };
      } else {
        workingRange = { ...workingRange, startIndex: targetIndex };
      }
    }
    return workingRange;
  };

  getParentMostFolderAndLeafFolders = (containers, targetFolderId) => {
    if (!Array.isArray(containers) || !targetFolderId) return null;
    for (const container of containers) {
      const path = this.findFolderPath(container?.folders, targetFolderId, []);
      if (path && path.length > 0) {
        const parentMostFolder = path[0];
        const leafFolders = this.collectLeafFolders(parentMostFolder);
        return { parentMostFolder, leafFolders };
      }
    }
    return null;
  };

  findFolderPath = (folders, targetFolderId, pathSoFar) => {
    if (!Array.isArray(folders) || folders.length === 0) return null;
    for (const folder of folders) {
      const nextPath = [...pathSoFar, folder];
      if (folder?.id === targetFolderId) {
        return nextPath;
      }
      const childPath = this.findFolderPath(folder?.folders, targetFolderId, nextPath);
      if (childPath) return childPath;
    }
    return null;
  };

  collectLeafFolders = (folder, acc = []) => {
    if (!folder) return acc;
    const children = Array.isArray(folder.folders) ? folder.folders : [];
    if (children.length === 0) {
      acc.push(folder);
      return acc;
    }
    children.forEach((child) => this.collectLeafFolders(child, acc));
    return acc;
  };

  resolveLeafFoldersForCurrentFolder = () => {
    const folderId = this.getFolderIdFromSearch() || this.props.folderId;
    const { containers } = this.props;
    if (!folderId || !Array.isArray(containers) || containers.length === 0) {
      if (this.state.leafFoldersInParent.length > 0 || this.state.parentMostFolder) {
        this.setState({
          parentMostFolder: null,
          leafFoldersInParent: [],
          leafNavigationRange: null,
        });
      }
      return;
    }
    const result = this.getParentMostFolderAndLeafFolders(containers, folderId);
    if (!result) {
      if (this.state.leafFoldersInParent.length > 0 || this.state.parentMostFolder) {
        this.setState({
          parentMostFolder: null,
          leafFoldersInParent: [],
          leafNavigationRange: null,
        });
      }
      return;
    }
    const { parentMostFolder, leafFolders } = result;
    this.setState((prevState) => {
      const prevParentId = prevState.parentMostFolder?.id || null;
      const nextParentId = parentMostFolder?.id || null;
      const prevLeafIds = (prevState.leafFoldersInParent || []).map((item) => item?.id);
      const nextLeafIds = (leafFolders || []).map((item) => item?.id);
      const sameParent = prevParentId === nextParentId;
      const sameLeaves =
        prevLeafIds.length === nextLeafIds.length &&
        prevLeafIds.every((id, index) => id === nextLeafIds[index]);
      const needsRangeReset = !sameParent || !sameLeaves || !prevState.leafNavigationRange;
      const nextRange =
        Array.isArray(leafFolders) && leafFolders.length > 1 && needsRangeReset
          ? (() => {
              const currentIndex = leafFolders.findIndex((folder) => folder?.id === folderId);
              if (currentIndex === -1) return null;
              return { startIndex: currentIndex, endIndex: currentIndex };
            })()
          : Array.isArray(leafFolders) && leafFolders.length > 1
            ? prevState.leafNavigationRange
            : null;
      if (sameParent && sameLeaves && nextRange === prevState.leafNavigationRange) return null;
      return { parentMostFolder, leafFoldersInParent: leafFolders, leafNavigationRange: nextRange };
    });
  };

  getPrevNextFileIds = (folderFiles) => {
    if (!Array.isArray(folderFiles) || folderFiles.length === 0) {
      return {
        prevFileId: null,
        nextFileId: null,
        prevFolderFileId: null,
        nextFolderFileId: null,
      };
    }
    const currentIndex = this.getNavigationIndexForCurrentFile(folderFiles);
    if (currentIndex === -1) {
      return {
        prevFileId: null,
        nextFileId: null,
        prevFolderFileId: null,
        nextFolderFileId: null,
      };
    }
    const prevFile = currentIndex > 0 ? folderFiles[currentIndex - 1] : null;
    const nextFile = currentIndex < folderFiles.length - 1 ? folderFiles[currentIndex + 1] : null;
    return {
      prevFileId: this.getFolderFileId(prevFile),
      nextFileId: this.getFolderFileId(nextFile),
      prevFolderFileId: this.getFolderFileIdForUrl(prevFile),
      nextFolderFileId: this.getFolderFileIdForUrl(nextFile),
    };
  };

  navigateToFolderFile = async (targetFileId, targetFolderFileId = null) => {
    if (!targetFileId) return;
    if (this.navInFlight) return;
    this.navInFlight = true;
    try {
      const caseId = this.props.caseId || this.props.case?.id;
      const folderId = this.getFolderIdFromSearch() || this.props.folderId;
      if (!caseId || !folderId) return;
      const basePath = history.location.pathname.replace(/\/case.*/, '');
      const searchParams = new URLSearchParams(history.location?.search || '');
      searchParams.set('folderId', folderId);
      if (targetFolderFileId) {
        searchParams.set('folderFileId', targetFolderFileId);
      } else {
        searchParams.delete('folderFileId');
      }
      // Ensure new document navigation starts at page 1 (prevent carry-over scroll).
      searchParams.set('goToPage', '1');
      const navMeta = this.getNavigationFileMeta(targetFileId, targetFolderFileId);
      if (navMeta) {
        let effectiveTab = navMeta.tab;
        let effectiveStartPage = navMeta.startPage;
        let effectiveGlobalPagePrefix = navMeta.globalPagePrefix;
        let effectivePageCount = navMeta.pageCount;

        // When globalPagePrefix is missing from folder files data, the tab value
        // is also unreliable. Fetch correct values from the tabs API instead.
        if (!effectiveGlobalPagePrefix) {
          try {
            const tabsRes = await api.get(
              `/cases/${caseId}/files/${targetFileId}/tabs?firstTrialbookOnly=true`,
            );
            if (tabsRes && !tabsRes.error && Array.isArray(tabsRes) && tabsRes.length > 0) {
              const firstTab = tabsRes[0];
              effectiveTab = firstTab.tab;
              effectiveGlobalPagePrefix = firstTab.globalPagePrefix;
              effectiveStartPage = firstTab.startPage;
              effectivePageCount = firstTab.pageCount;
              this.skipNextFolderSpecificFileDetails = true;
            }
          } catch (err) {
            Logger.ERROR('Fetching tabs for navigation fallback', err);
          }
        }

        if (effectiveTab !== undefined && effectiveTab !== null) {
          searchParams.set('tab', `${effectiveTab}`);
        } else {
          searchParams.delete('tab');
        }
        if (effectiveStartPage !== undefined && effectiveStartPage !== null) {
          searchParams.set('startPage', `${effectiveStartPage}`);
        } else {
          searchParams.delete('startPage');
        }
        if (effectiveGlobalPagePrefix) {
          searchParams.set('globalPagePrefix', `${effectiveGlobalPagePrefix}`);
        } else {
          searchParams.delete('globalPagePrefix');
        }
        let pageCount = effectivePageCount;
        const parsedPageCount = parseInt(pageCount, 10);
        if (!pageCount || Number.isNaN(parsedPageCount) || parsedPageCount === 0) {
          const lastPage = parseInt(navMeta.lastPage, 10);
          const startPage = parseInt(effectiveStartPage, 10);
          if (!Number.isNaN(lastPage) && !Number.isNaN(startPage)) {
            pageCount = `${Math.max(0, lastPage - startPage)}`;
          }
        }
        if (pageCount !== undefined && pageCount !== null) {
          searchParams.set('pageCount', `${pageCount}`);
        } else {
          searchParams.delete('pageCount');
        }
      }
      history.push(`${basePath}/case/${caseId}/files/${targetFileId}?${searchParams.toString()}`);
    } finally {
      this.navInFlight = false;
    }
  };

  handleAttachmentDocumentSelect = (targetDocument, source) => {
    const targetFileId =
      targetDocument?.fileId ||
      targetDocument?.id ||
      targetDocument?.file?.fileId ||
      targetDocument?.file?.id ||
      targetDocument?.folderFileId;
    if (!targetFileId) return;
    const sourceDocId = this.getResolvedFileIdFromMeta(this.state.currentFileMetaData);
    const sourceDocName =
      this.state.currentFileMetaData?.name ||
      this.state.currentFileMetaData?.fileName ||
      this.state.currentFileMetaData?.file?.name ||
      '';
    const sourceDocFolderFileId =
      this.state.currentFileMetaData?.folderFileId || this.state.currentFileMetaData?.file?.folderFileId;
    const targetIdCandidates = [
      targetFileId,
      targetDocument?.id,
      targetDocument?.fileId,
      targetDocument?.file?.id,
      targetDocument?.file?.fileId,
    ]
      .filter((id) => id !== null && id !== undefined)
      .map((id) => String(id));
    const uniqueTargetIds = Array.from(new Set(targetIdCandidates));
    if (sourceDocId && source) {
      this.setState((prevState) => ({
        attachmentIndicatorHintsByFileId: uniqueTargetIds.reduce((nextHints, nextTargetId) => {
          nextHints[nextTargetId] =
            source === 'attachments'
              ? {
                  attachments: [],
                  isAttachedTo: [
                    { id: sourceDocId, name: sourceDocName, folderFileId: sourceDocFolderFileId },
                  ],
                }
              : {
                  attachments: [
                    { id: sourceDocId, name: sourceDocName, folderFileId: sourceDocFolderFileId },
                  ],
                  isAttachedTo: [],
                };
          return nextHints;
        }, { ...prevState.attachmentIndicatorHintsByFileId }),
      }));
    }
    const caseId = this.props.caseId || this.props.case?.id;
    if (caseId && targetFileId) {
      api
        .get(`/cases/${caseId}/files/${targetFileId}/details?includeAttachments=true`)
        .then((res) => {
          if (!res || res.error) return;
          const resolvedHintFileId = this.getResolvedFileIdFromMeta(res) || targetFileId;
          const { attachments, attachedToDocuments } = this.getAttachmentRelationships(res);
          const normalizedAttachments = Array.isArray(attachments) ? attachments : [];
          const normalizedAttachedTo = Array.isArray(attachedToDocuments) ? attachedToDocuments : [];
          const hintIds = Array.from(new Set([...uniqueTargetIds, String(resolvedHintFileId)]));
          this.setState((prevState) => ({
            attachmentIndicatorHintsByFileId: hintIds.reduce((nextHints, nextHintId) => {
              nextHints[nextHintId] = {
                attachments: normalizedAttachments,
                isAttachedTo: normalizedAttachedTo,
              };
              return nextHints;
            }, { ...prevState.attachmentIndicatorHintsByFileId }),
          }));
        })
        .catch(() => {});
    }
    const targetFolderFileId =
      targetDocument?.folderFileId || targetDocument?.file?.folderFileId || null;
    const folderId = this.getFolderIdFromSearch() || this.props.folderId;
    if (folderId) {
      this.navigateToFolderFile(targetFileId, targetFolderFileId);
      setTimeout(() => this.focusViewerShellForKeyboardShortcuts(), 0);
      return;
    }
    if (!caseId) return;
    const basePath = history.location.pathname.replace(/\/case.*/, '');
    const searchParams = new URLSearchParams(history.location?.search || '');
    searchParams.delete('tab');
    searchParams.delete('startPage');
    searchParams.delete('globalPagePrefix');
    searchParams.delete('pageCount');
    searchParams.set('goToPage', '1');
    if (targetFolderFileId) {
      searchParams.set('folderFileId', targetFolderFileId);
    } else {
      searchParams.delete('folderFileId');
    }
    history.push(
      `${basePath}/case/${caseId}/files/${targetFileId}${
        searchParams.toString() ? `?${searchParams.toString()}` : ''
      }`,
    );
    setTimeout(() => this.focusViewerShellForKeyboardShortcuts(), 0);
  };

  fetchFolderFiles = async (folderId, fetchKey) => {
    if (!folderId || folderId === 'recordings') return;
    const caseId = this.props.caseId || this.props.case?.id;
    if (!caseId) return;
    if (this.folderFilesInFlight[folderId]) return;
    this.folderFilesInFlight[folderId] = true;
    if (fetchKey) {
      this.folderFilesFetchKeyById[folderId] = fetchKey;
    }
    const clearInFlight = () => {
      this.folderFilesInFlight[folderId] = false;
    };

    try {
      const res = await api.get(
        `/cases/${caseId}/folders/${folderId}/files?includeBundleLocations=true`,
      );
      if (res && res.error) {
        Logger.ERROR('Fetching Folder Files', res.error);
        if (fetchKey) {
          delete this.folderFilesFetchKeyById[folderId];
        }
        return null;
      }
      const files = Array.isArray(res) ? res : res?.results || [];
      this.setState((prevState) => ({
        folderFilesById: { ...prevState.folderFilesById, [folderId]: files },
      }));
      return files;
    } catch (error) {
      Logger.ERROR('Fetching Folder Files', error);
      if (fetchKey) {
        delete this.folderFilesFetchKeyById[folderId];
      }
      return null;
    } finally {
      clearInFlight();
    }
  };

  maybeFetchFolderFiles = () => {
    const folderId = this.getFolderIdFromSearch() || this.props.folderId;
    if (!folderId || folderId === 'recordings') return;
    const caseId = this.props.caseId || this.props.case?.id;
    if (!caseId) return;
    const cachedFiles = this.getCachedFolderFiles(folderId);
    if (cachedFiles !== null) return;
    const storedFiles = this.getStoredFolderFiles(folderId);
    if (storedFiles && storedFiles.length > 0) return;
    const isExactUrl = this.isExactFolderFileUrl();
    const currentSearch = history.location?.search || '';
    const fetchKey = isExactUrl ? `exact:${folderId}` : `non-exact:${folderId}:${currentSearch}`;
    if (this.folderFilesFetchKeyById[folderId] === fetchKey) return;
    this.fetchFolderFiles(folderId, fetchKey);
  };

  getSidebarVisibility = () => {
    const currentUrl = this.props?.history?.location?.pathname;
    const isOnFullFileScreen = /^\/case\/[^/]+\/files\/[^/]+$/.test(currentUrl);
    const isSharing = this.props?.history?.location?.search?.includes('view=share');
    const showSidebar = isOnFullFileScreen && !isSharing;
    return { isOnFullFileScreen, isSharing, showSidebar };
  };

  getPanelSizes = () => {
    const { showSidebar } = this.getSidebarVisibility();
    const hasCompare = this.props.fileToCompare && Object.keys(this.props.fileToCompare).length > 0;
    const width = typeof window !== 'undefined' ? window.innerWidth : null;
    const isCompactScreen = width !== null && width <= compactViewerScreenWidth;
    const isSmallScreen = width !== null && width <= smallViewerScreenWidth;
    if (isCompactScreen) {
      if (hasCompare && showSidebar) return [60, 20, 20];
      if (hasCompare && !showSidebar) return [60, 40];
      if (showSidebar) return [60, 40];
      return [100, 0];
    }
    if (isSmallScreen) {
      if (hasCompare && showSidebar) return [70, 15, 15];
      if (hasCompare && !showSidebar) return [70, 30];
      if (showSidebar) return [70, 30];
      return [100, 0];
    }
    if (hasCompare && showSidebar) return [80, 10, 10];
    if (hasCompare && !showSidebar) return [80, 20];
    if (showSidebar) return [80, 20];
    return [100, 0];
  };

  render() {
    const { isSharing, showSidebar } = this.getSidebarVisibility();
    const matchingFolder = this.getMatchingFolderFromQueryParams();

    const startPageForGlobalPaging =
      !this.props.isBundleTabPage && matchingFolder ? matchingFolder.startPage : null;

    const isAiTools = this.props?.url?.includes('/ai-tools');
    const isSearchMode = this.props?.url?.includes('/search-mode');
    const currentPath = history.location?.pathname || '';
    const currentSearch = history.location?.search || '';
    const searchParams = new URLSearchParams(currentSearch);
    const isBigViewScreen =
      !!this.props.folderId &&
      this.props.folderId !== 'recordings' &&
      /^\/case\/[^/]+\/files\/[^/]+$/.test(currentPath) &&
      searchParams.get('view') !== 'share';
    const folderFiles = this.getFolderFilesForNavigation();
    const { prevFileId, nextFileId, prevFolderFileId, nextFolderFileId } =
      this.getPrevNextFileIds(folderFiles);
    const prevFileMeta = prevFileId
      ? this.getNavigationFileMeta(prevFileId, prevFolderFileId)
      : null;
    const nextFileMeta = nextFileId
      ? this.getNavigationFileMeta(nextFileId, nextFolderFileId)
      : null;
    const prevTooltipTitle = this.getNavigationTooltipTitle(
      prevFileMeta,
      prevFileId,
      'generic.prevDoc',
    );
    const nextTooltipTitle = this.getNavigationTooltipTitle(
      nextFileMeta,
      nextFileId,
      'generic.nextDoc',
    );
    const isCurrentFile = !!(
      this.props.fileId &&
      this.state.currentFileMetaData &&
      (this.props.fileId === this.state.currentFileMetaData?.id ||
        this.props.fileId === this.state.currentFileMetaData?.file?.id)
    );
    const { attachments: attachmentsSource, attachedToDocuments: attachedToSource } =
      this.getAttachmentRelationships(this.state.currentFileMetaData);
    const resolvedCurrentFileId =
      this.getResolvedFileIdFromMeta(this.state.currentFileMetaData) ||
      this.props.fileId ||
      this.props.folderFileId ||
      null;
    const attachmentHintKeys = Array.from(
      new Set(
        [resolvedCurrentFileId, this.props.fileId, this.props.folderFileId]
          .filter((id) => id !== null && id !== undefined)
          .map((id) => String(id)),
      ),
    );
    const attachmentHintsForFile = attachmentHintKeys.reduce((foundHint, hintKey) => {
      if (foundHint) return foundHint;
      return this.state.attachmentIndicatorHintsByFileId?.[hintKey] || null;
    }, null);
    const hintedAttachments = Array.isArray(attachmentHintsForFile?.attachments)
      ? attachmentHintsForFile.attachments
      : [];
    const hintedAttachedTo = Array.isArray(attachmentHintsForFile?.isAttachedTo)
      ? attachmentHintsForFile.isAttachedTo
      : [];
    const attachments = Array.isArray(attachmentsSource)
      ? attachmentsSource.length > 0
        ? attachmentsSource
        : hintedAttachments
      : hintedAttachments;
    const attachedToDocuments = Array.isArray(attachedToSource)
      ? attachedToSource.length > 0
        ? attachedToSource
        : hintedAttachedTo
      : hintedAttachedTo;
    const confidentialityRingNode =
      this.props.isConfidentialityRing &&
      this.state.currentFileMetaData &&
      this.state.currentFileMetaData.confidentialityRing ? (
        <FontAwesomeIcon
          style={{
            color: cfRingColorMapping[this.state.currentFileMetaData.confidentialityRing],
            paddingTop: '0.2rem',
          }}
          icon={faCircle}
        />
      ) : null;
    const attachmentIndicatorsNode =
      !this.props.isRecordingsPage && !isSharing ? (
        <AttachmentIndicators
          attachments={attachments}
          attachedToDocuments={attachedToDocuments}
          onSelectDocument={this.handleAttachmentDocumentSelect}
        />
      ) : null;
    const hasCompare = this.props.fileToCompare && Object.keys(this.props.fileToCompare).length > 0;
    return (
      !this.props.fetchCasesPending && (
        <div className="case-theatre" ref={this.keyboardFocusRef} tabIndex={-1}>
          <NavBar
            hasSearch
            isSharing={isSharing}
            search={this.state.searchDetails}
            searchFunctions={this.state.searchFunctionsMain}
            brandText={
              window.location.pathname.includes('/present-view') ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FontAwesomeIcon
                    icon={faHome}
                    style={{ color: '#666', cursor: 'pointer', fontSize: '1.5rem' }}
                    onClick={() => history.push(`/case/${this.props.caseId}`)}
                    title="Back to case"
                  />
                  {this.getBrandText(isSharing)}
                </span>
              ) : this.getBrandText(isSharing)
            }
            confidentialityRingLabel={
              confidentialityRingNode || attachmentIndicatorsNode ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0.15rem' }}>
                  {confidentialityRingNode}
                  {attachmentIndicatorsNode}
                </span>
              ) : (
                ''
              )
            }
            bundleLocations={
              !this.props.isRecordingsPage &&
              !isSharing && (
                <BundleLocations
                  bundleLocations={this.state.bundleLocations}
                  fileId={this.props.fileId}
                  folderFileId={this.props.folderFileId}
                  filePageCount={this.state.filePageCount}
                />
              )
            }
          />
          {/* Trigger in-document search/highlight when Redux docSearch updates */}
          {(this.props.docSearchVal || this.props.docSearchLocation) &&
            this.state.searchFunctionsMain &&
            this.state.currentFileMetaData &&
            isCurrentFile && (
              <DocumentSearch
                searchFunctions={this.state.searchFunctionsMain}
                docSearchVal={this.props.docSearchVal}
                docSearchLocation={this.props.docSearchLocation}
                isAiTools={!!isAiTools}
                isSearchMode={!!isSearchMode}
                sideSummaryOpen={false}
              />
            )}
          {/* Compare document search/highlight support */}
          {this.props.fileToCompare &&
            Object.keys(this.props.fileToCompare).length > 0 &&
            this.state.searchFunctionsCompare && (
              <DocumentSearch
                searchFunctions={this.state.searchFunctionsCompare}
                docSearchVal={this.props.docSearchValForCompare}
                docSearchLocation={this.props.docSearchLocationForCompare}
                isAiTools={!!isAiTools}
                isSearchMode={!!isSearchMode}
                sideSummaryOpen={false}
              />
            )}
          <div style={{ height: 'calc(100% - 7rem)' }}>
            <style>
              {`
                .sash, .sash:hover, .sash::before {
                  background-color: #E2E2E2 !important;
                }
                .sash-vertical::after {
                  content: '...';
                  writing-mode: vertical-rl;
                  text-orientation: sideways;
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-30%, -50%);
                  font-size: 30px;
                  color: #888;
                }
              `}
            </style>
            <Allotment
              key={`layout-${hasCompare ? 'with-compare' : 'no-compare'}-${showSidebar ? 'with-sidebar' : 'no-sidebar'}`}
              defaultSizes={this.getPanelSizes()}
              className="custom-allotment"
              style={{
                '--separator-border': '1px solid #E2E2E2',
                '--separator-hover-border': '1px solid #E2E2E2',
              }}
            >
              <Allotment.Pane minSize={100}>
                {(!this.props.fileId || this.state.currentFileMetaData) && (
                  <div className="viewer-document-pane">
                    {this.props.fileToCompare &&
                      Object.keys(this.props.fileToCompare).length > 0 && (
                        <div
                          style={{
                            height: '40px',
                            padding: '6px 10px',
                            fontSize: '0.9rem',
                            color: '#555',
                            borderBottom: '1px solid #E2E2E2',
                            backgroundColor: '#EBF3FE',
                          }}
                        >
                          {this.state.currentFileMetaData?.id}
                        </div>
                      )}
                    <div className="viewer-document-card">
                      <FileViewer
                        bundleSpecificFileDetails={this.state.bundleSpecificFileDetails}
                        searchHandler={this.searchHandler}
                        searchFunctionsHandler={this.searchFunctionsHandlerMain}
                        isFullScreen
                        isQueryParamMatchingAnyFolder={this.isQueryParamMatchingAnyFolder()}
                        currentFileMetaData={this.normalizeFileMeta(this.state.currentFileMetaData)}
                        startPageForGlobalPaging={startPageForGlobalPaging}
                      />
                      {isBigViewScreen && (
                        <>
                          <Tooltip
                            title={prevTooltipTitle || ''}
                            placement="top"
                            arrow
                            disableHoverListener={!prevTooltipTitle}
                          >
                            <span className="viewer-document-nav viewer-document-nav--prev">
                              <IconButton
                                id="updateFilePrevButton"
                                className="viewer-document-nav-button"
                                onClick={() =>
                                  this.navigateToFolderFile(prevFileId, prevFolderFileId)
                                }
                                disabled={!prevFileId}
                                aria-label={T.translate('generic.prevDoc')}
                              >
                                <FontAwesomeIcon icon={faChevronLeft} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={nextTooltipTitle || ''}
                            placement="top"
                            arrow
                            disableHoverListener={!nextTooltipTitle}
                          >
                            <span className="viewer-document-nav viewer-document-nav--next">
                              <IconButton
                                id="updateFileNextButton"
                                className="viewer-document-nav-button"
                                onClick={() =>
                                  this.navigateToFolderFile(nextFileId, nextFolderFileId)
                                }
                                disabled={!nextFileId}
                                aria-label={T.translate('generic.nextDoc')}
                              >
                                <FontAwesomeIcon icon={faChevronRight} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </Allotment.Pane>

              <Allotment.Pane minSize={showSidebar ? 100 : 0}>
                {showSidebar && (
                  <RightSidebarViewerWithScroll
                    fileData={this.state.currentFileMetaData}
                    viewerControl={this.props.viewerControl}
                    gridView={false}
                  />
                )}
              </Allotment.Pane>

              {this.props.fileToCompare && Object.keys(this.props.fileToCompare).length > 0 && (
                <Allotment.Pane minSize={100}>
                  <div
                    style={{
                      height: '40px',
                      padding: '6px 9px',
                      fontSize: '0.9rem',
                      color: '#555',
                      borderBottom: '1px solid #E2E2E2',
                      backgroundColor: '#EBFEF7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>{this.props.fileToCompare?.id}</span>
                    <IconButton
                      size="small"
                      onClick={() => this.props.actions.setFileToCompare(null)}
                      sx={{
                        padding: '2px',
                        minWidth: 'auto',
                        marginLeft: '8px',
                        marginRight: '8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </div>
                  <FileViewer
                    bundleSpecificFileDetails={{}}
                    searchHandler={this.searchHandler}
                    searchFunctionsHandler={this.searchFunctionsHandlerCompare}
                    isFullScreen
                    isQueryParamMatchingAnyFolder={false}
                    currentFileMetaData={this.normalizeFileMeta(this.props.fileToCompare)}
                    startPageForGlobalPaging={null}
                    isFileToCompare
                    fileData={this.props.fileToCompare}
                  />
                </Allotment.Pane>
              )}
            </Allotment>
          </div>
          <Footer />
        </div>
      )
    );
  }
}

/* istanbul ignore next */
function mapStateToProps(state) {
  const {
    home: { cases, fetchCasesPending },
    matcher: {
      params: { case: currentCase, file, url },
      query: { folderId, folderFileId, tab, startPage, globalPagePrefix, goToPage },
    },
    case: { folderSpecificFileDetails, documents },
  } = state;
  const currentUserCR = selectCurrentUserConfidentialityRing(state);
  const isBundleTabPage = selectBundleTabPage(state);
  const isConfidentialityRing = selectConfidentialityRing(state) && currentUserCR !== 'noRing';
  return {
    case: cases.find((c) => c.id === currentCase) || {},
    caseId: currentCase,
    fetchCasesPending,
    fileId: file,
    url,
    folderId,
    folderFileId,
    isRecordingsPage: folderId === 'recordings',
    tab,
    startPage,
    globalPagePrefix,
    goToPage,
    isBundleTabPage,
    isConfidentialityRing,
    folderSpecificFileDetails,
    viewerControl: selectViewerControl(state),
    docSearchVal: selectDocSearchVal(state),
    docSearchLocation: selectDocSearchLocation(state),
    fileToCompare: selectFileToCompare(state),
    docSearchValForCompare: state.viewing?.docSearchForCompare || '',
    docSearchLocationForCompare: state.viewing?.docSearchLocationForCompare || null,
    documents,
    containers: selectFolderContainers(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
    actionsV: bindActionCreators({ ...actionsV }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withAuth(Viewer, { forceLogin: true }));
